<template>
  <div>
    <div v-if="hasShadowData">
      <ReportDashboardShadow />
    </div>
    <div v-else class="shadow-report-empty">
      <h2 class="shadow-report-title">Shadow Report Playground</h2>
      <p class="shadow-report-copy">
        No report data was found in local storage. Open the live customer report first so the app persists the
        real `cpt-vehicle-reports` payload, then reload this shadow path.
      </p>
      <p class="shadow-report-copy">
        Required keys: `cpt-vehicle-reports`, `cpt-active-report-vin`, and optionally `cpt-session-id`.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import ReportDashboardShadow from '@/components/ReportDashboardShadow.vue';
import { useAppStore } from '@/stores/app';
import type { VehicleReport } from '@/types';

const store = useAppStore();

const hasShadowData = computed(() => Object.keys(store.vehicleReports).length > 0);

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

onMounted(() => {
  const reports = readJson<Record<string, VehicleReport>>('cpt-vehicle-reports', {});
  const activeVin = readJson<string | null>('cpt-active-report-vin', null);
  const sessionId = readJson<string | null>('cpt-session-id', null);
  const selectedPlan = readJson<string | null>('cpt-selected-plan', null);

  if (Object.keys(reports).length === 0) {
    return;
  }

  store.setVehicleReports(reports);
  store.setActiveReportVin(activeVin || Object.keys(reports)[0] || null);
  if (sessionId) {
    store.setSessionId(sessionId);
  }
  if (selectedPlan) {
    store.setSelectedPlan(selectedPlan);
  }
  store.setStep('report');
  store.setError(null);
});
</script>

<style scoped>
.shadow-plan-switch {
  max-width: 820px;
  margin: 2rem auto 1rem;
  display: flex;
  gap: 0.75rem;
}

.shadow-plan-button {
  padding: 0.65rem 1rem;
  border: 1px solid rgba(255,255,255,0.12);
  background: var(--bg);
  color: var(--white);
  cursor: pointer;
}

.shadow-plan-button.active {
  background: var(--white);
  color: var(--bg);
  border-color: var(--white);
}

.shadow-report-empty {
  max-width: 820px;
  margin: 4rem auto;
  padding: 2rem;
  border: 1px solid rgba(255,255,255,0.12);
  background: var(--bg);
  color: var(--white);
}

.shadow-report-title {
  margin: 0 0 1rem;
  font-size: 2rem;
  line-height: 1.1;
}

.shadow-report-copy {
  margin: 0 0 0.75rem;
  line-height: 1.5;
}
</style>
